import 'react';
import { Button, Stack } from '@mui/material';
import { DateRangePicker } from './advancedFilters/DateRangePicker';

import { DropdownList } from './advancedFilters/DropdownList';
import { Preset } from '../../../../common/models/valueObjects/Preset';
import { FilterAltOutlined, LocalOfferOutlined, MotionPhotosOffOutlined, SourceOutlined } from '@mui/icons-material';
import { AdvancedDashboardFilters as AdvancedDashboardFiltersSkeleton } from '../skeletons/AdvancedDashboardFilters';

import { t } from 'i18next';
import { DateRange } from '../../../../common/dashboards/types';

type Props = {
  availableExceptions: Preset[];
  availableOverviewTypes: Preset[];

  tags: Preset[];
  projects: Preset[];
  profiles: Preset[];
  exceptions: Preset[];
  overviewTypes: Preset[];

  minDate?: string;
  dateRange: DateRange;
  dateRangeLabel: string;

  fetchProjects: (query: string, exclude: string[], limit: number) => Promise<Preset[]>;
  fetchTags: (query: string, exclude: string[], limit: number) => Promise<Preset[]>;
  fetchProfiles: (query: string, exclude: string[], limit: number) => Promise<Preset[]>;

  onUpdateDateRange: (value: { range: DateRange; label: string }) => void;
  onUpdateExceptions: (exceptions: Preset[]) => void;
  onUpdateTags: (tags: Preset[]) => void;
  onUpdateProjects: (projects: Preset[]) => void;
  onUpdateProfiles: (profiles: Preset[]) => void;
  onUpdateOverviewTypes: (exceptions: Preset[]) => void;

  onResetFilters: () => void;

  initialLoad: boolean;
  loading: boolean;
};

export const AdvancedDashboardFilters = ({
  availableExceptions,
  availableOverviewTypes,

  tags,
  projects,
  profiles,
  exceptions,
  overviewTypes,

  minDate,
  dateRange,
  dateRangeLabel,

  fetchProjects,
  fetchTags,
  fetchProfiles,

  onUpdateDateRange,
  onUpdateExceptions,
  onUpdateTags,
  onUpdateProjects,
  onUpdateProfiles,
  onUpdateOverviewTypes,

  onResetFilters,
  initialLoad,
  loading,
}: Props) => {
  if (initialLoad) {
    return <AdvancedDashboardFiltersSkeleton />;
  }

  return (
    <Stack
      justifyContent={'start'}
      alignItems={{ xs: 'stretch', md: 'center' }}
      direction={{ xs: 'column', md: 'row' }}
      flexWrap={'wrap'}
      gap={1}
      sx={{ mb: 2.25 }}
    >
      <DropdownList
        onChange={(value) => onUpdateProjects(value)}
        value={projects}
        buttonLabels={{
          emptyState: t('advancedFilters.fields.allProjects'),
          nonEmptyPrefix: t('advancedFilters.fields.projects'),
        }}
        prefixIcon={<SourceOutlined />}
        search={{
          placeholder: t('advancedFilters.fields.searchProjects'),
          onSearch: fetchProjects,
          runOnMount: true,
        }}
        disabled={loading}
      />
      <DateRangePicker minDate={minDate} onChange={(value) => onUpdateDateRange(value)} value={dateRange} label={dateRangeLabel} disabled={loading} />
      <DropdownList
        onChange={(value) => onUpdateTags(value)}
        value={tags}
        buttonLabels={{
          emptyState: t('advancedFilters.fields.tags'),
          nonEmptyPrefix: t('advancedFilters.fields.tags'),
        }}
        prefixIcon={<LocalOfferOutlined />}
        search={{
          placeholder: t('advancedFilters.fields.searchTags'),
          onSearch: fetchTags,
          runOnMount: true,
        }}
        disabled={loading}
      />
      <DropdownList
        onChange={(value) => onUpdateProfiles(value)}
        value={profiles}
        buttonLabels={{
          emptyState: t('advancedFilters.fields.profiles'),
          nonEmptyPrefix: t('advancedFilters.fields.profiles'),
        }}
        prefixIcon={<SourceOutlined />}
        search={{
          placeholder: t('advancedFilters.fields.searchProfiles'),
          onSearch: fetchProfiles,
          runOnMount: true,
        }}
        disabled={loading}
      />
      <DropdownList
        onChange={(value) => onUpdateExceptions(value)}
        defaultItems={availableExceptions}
        value={exceptions}
        buttonLabels={{
          emptyState: t('advancedFilters.fields.exceptions'),
          nonEmptyPrefix: t('advancedFilters.fields.exceptions'),
        }}
        prefixIcon={<MotionPhotosOffOutlined />}
        hideReset={true}
        disabled={loading}
      />
      <DropdownList
        onChange={(value) => onUpdateOverviewTypes(value)}
        defaultItems={availableOverviewTypes}
        value={overviewTypes}
        buttonLabels={{
          emptyState: t('advancedFilters.fields.types'),
          nonEmptyPrefix: t('advancedFilters.fields.types'),
        }}
        prefixIcon={<FilterAltOutlined />}
        hideReset={true}
        allowSelectAll={true}
        disabled={loading}
      />
      <Button
        variant="contained"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();

          onResetFilters();
        }}
        disabled={loading}
      >
        {t('resetFilters')}
      </Button>
    </Stack>
  );
};
